import { UrbanFeatureType } from "@/types/gql/graphql";
import { EditableGeoJsonLayer, ViewMode, EditAction } from "@deck.gl-community/editable-layers";
import { Feature, FeatureCollection, LineString, Point, Polygon } from "geojson";
import { UrbanFeature, UrbanFeatureMode, UrbanFeatureModeType } from "../urban-feature.types";
import { capitalizeFirstLetterOfEachWord, convertLineToPolygon } from "@/helper/format-data";
import { cloneDeep } from "lodash";
import {
  crosswalkModes,
  urbanFeaturePolygonAddModes,
  urbanFeaturePolygonEditModes,
  urbanFeaturePolygonEditorLayerModes,
  urbanFeaturePolygonModes,
} from "../urban-feature.const";
import { useContext, useEffect, useMemo, useState } from "react";

import { CityContext } from "@/contexts/city-context-provider";
import { GeoJsonLayer } from "@deck.gl/layers";
import { UrbanFeatureTooltipProps } from "@/modules/urban-feature/components/urban-feature-tooltip";
import { filterFeatureIndex } from "@/helper/map-utils";
import { getAddress } from "@/services/web/mapbox.service";
import { useQueryUrbanFeature } from "./use-query-urban-feature.hook";

const urbanFeaturePolygonEditableLayerName = "urbanFeaturePolygon-editable-layer";

export const useUrbanFeaturePolygon = ({
  urbanFeatureMode,
  selectedUrbanTypes,
}: {
  urbanFeatureMode: UrbanFeatureMode;
  selectedUrbanTypes: UrbanFeatureType[];
}) => {
  const { currentCity } = useContext(CityContext);
  const [openUrbanFeaturePolygonModal, setOpenUrbanFeaturePolygonModal] = useState(false);
  const [urbanFeaturePolygonEditionData, setUrbanFeaturePolygonEditionData] = useState<
    FeatureCollection<LineString, UrbanFeature>
  >({
    type: "FeatureCollection",
    features: [],
  });
  const [urbanFeaturePolygonData, setUrbanFeaturePolygonData] = useState<Feature<Polygon, UrbanFeature>[]>([]);
  const [urbanFeaturePolygonDetail, setUrbanFeaturePolygonDetail] = useState<Feature<LineString, UrbanFeature> | null>(
    null,
  );
  const [hoverUrbanFeaturePolygonInfo, setHoverUrbanFeaturePolygonInfo] = useState<UrbanFeatureTooltipProps | null>(
    null,
  );

  const {
    urbanFeatureList,
    loading: urbanFeaturePolygonLoading,
    createUrbanFeature,
    updateUrbanFeature,
    removeUrbanFeature,
  } = useQueryUrbanFeature({
    types: [UrbanFeatureType.Crosswalk, UrbanFeatureType.SpeedBump],
  });

  const currentSelectedIndexes = useMemo(() => {
    if (!urbanFeaturePolygonDetail) {
      return [];
    }
    return filterFeatureIndex(urbanFeaturePolygonEditionData, urbanFeaturePolygonDetail);
  }, [urbanFeaturePolygonEditionData, urbanFeaturePolygonDetail]);

  const editableGeoJsonLayerMode = useMemo(() => {
    return (
      urbanFeaturePolygonEditorLayerModes[urbanFeatureMode as keyof typeof urbanFeaturePolygonEditorLayerModes] ??
      ViewMode
    );
  }, [urbanFeatureMode]);

  const resetDataOfEditState = () => {
    if (urbanFeaturePolygonAddModes.includes(urbanFeatureMode)) {
      //Delete the previously drawn urban feature on the map
      setUrbanFeaturePolygonEditionData((prev) => ({
        type: "FeatureCollection",
        features: prev.features.filter((feature) => feature.id),
      }));
    }
    setUrbanFeaturePolygonDetail(null);
    setOpenUrbanFeaturePolygonModal(false);
  };

  const cancelUrbanFeaturePolygonChange = () => {
    resetDataOfEditState();
  };

  const onEditUrbanFeaturePolygonLayer = async ({
    updatedData,
    editType,
  }: EditAction<FeatureCollection<LineString, UrbanFeature>>) => {
    setUrbanFeaturePolygonEditionData(updatedData);
    let selectedIndex = currentSelectedIndexes[0];
    if (urbanFeaturePolygonEditModes.includes(urbanFeatureMode)) {
      /** Re-calculate the polygon when changing the shape of curb-zone */
      const clonedFeature = cloneDeep(updatedData.features[selectedIndex]) as Feature<LineString, UrbanFeature>;
      const newPolygon = convertLineToPolygon(clonedFeature, clonedFeature.properties?.width || 2);
      updatedData.features[selectedIndex].properties.geometry = newPolygon.geometry;
      updatedData.features[selectedIndex].properties.editLine = newPolygon.properties.editLine;
      setUrbanFeaturePolygonEditionData(updatedData);
    } else {
      setUrbanFeaturePolygonEditionData(updatedData);
    }

    if (editType === "finishMovePosition" || editType === "addFeature") {
      if (urbanFeaturePolygonAddModes.includes(urbanFeatureMode)) {
        selectedIndex = updatedData.features.length - 1;
      }
      const newUrbanFeaturePolygon = updatedData.features[selectedIndex];
      const coordinates = newUrbanFeaturePolygon?.geometry?.coordinates[0];
      const pointOfUrbanFeature: Point = { type: "Point", coordinates };
      const address = await getAddress(pointOfUrbanFeature);
      address.name = capitalizeFirstLetterOfEachWord(address.name);
      const type = crosswalkModes.includes(urbanFeatureMode) ? UrbanFeatureType.Crosswalk : UrbanFeatureType.SpeedBump;
      const newUrbanFeature: Feature<LineString, UrbanFeature> = {
        ...newUrbanFeaturePolygon,
        properties: {
          ...newUrbanFeaturePolygon.properties,
          address: {
            streetNumber: address?.number ?? "",
            streetName: address?.name ?? "",
            city: address.city ?? "",
            postalCode: address.postcode ?? "",
            country: address.country ?? "",
          },
          name: newUrbanFeaturePolygon.properties.name ?? "",
          cityId: currentCity?.id ?? "",
          type,
        },
        geometry: newUrbanFeaturePolygon.geometry,
      };
      setUrbanFeaturePolygonDetail({ ...updatedData.features[selectedIndex], properties: newUrbanFeature.properties });
      if (urbanFeaturePolygonAddModes.includes(urbanFeatureMode)) {
        setOpenUrbanFeaturePolygonModal(true);
      } else {
        //Automatically save changes when editing lines on the map
        await saveUrbanFeaturePolygon(newUrbanFeature.properties, updatedData.features[selectedIndex]);
      }
    }
  };

  const saveUrbanFeaturePolygon = async (input: UrbanFeature, editedLine?: Feature<LineString, UrbanFeature>) => {
    /**
     * editedLine: for the edit line in the map, don't re-calculate to get the polygon
     * **/

    if (!urbanFeaturePolygonDetail || !currentCity?.id) {
      if (openUrbanFeaturePolygonModal) {
        setOpenUrbanFeaturePolygonModal(false);
      }
      return;
    }
    const clonedDetail = cloneDeep(urbanFeaturePolygonDetail) as Feature<LineString, UrbanFeature>;
    const urbanFeaturePolygon = editedLine ?? convertLineToPolygon(clonedDetail, input.width);
    const createUrbanFeaturesInput = {
      address: input.address,
      cityId: currentCity?.id,
      name: input.name,
      type: input.type,
      editLine: urbanFeaturePolygon.properties.editLine,
      width: input.width,
      geometry: editedLine?.properties.geometry ?? urbanFeaturePolygon.geometry,
    };

    if (urbanFeaturePolygonAddModes.includes(urbanFeatureMode)) {
      const lastUrbanFeaturePolygonFeature = urbanFeaturePolygonEditionData.features.at(-1);
      if (!lastUrbanFeaturePolygonFeature) return;
      await createUrbanFeature({ variables: { createUrbanFeaturesInput } });
    } else if (input.id) {
      const updateUrbanFeatureInput = {
        ...createUrbanFeaturesInput,
        id: input.id,
      };
      await updateUrbanFeature({ variables: { updateUrbanFeatureInput } });
      resetDataOfEditState();
    }
    if (openUrbanFeaturePolygonModal) {
      setOpenUrbanFeaturePolygonModal(false);
    }
  };

  const handleRemoveUrbanFeaturePolygon = async () => {
    if (urbanFeaturePolygonDetail?.id) {
      await removeUrbanFeature({ variables: { urbanFeatureId: urbanFeaturePolygonDetail.id as string } });
    }
    setUrbanFeaturePolygonDetail(null);
  };

  const isVisibleUrbanFeaturePolygonLayer = useMemo(() => {
    return urbanFeaturePolygonModes.includes(urbanFeatureMode);
  }, [urbanFeatureMode]);

  const editableUrbanFeaturePolygonLayer = new EditableGeoJsonLayer({
    id: urbanFeaturePolygonEditableLayerName,
    data: {
      type: urbanFeaturePolygonEditionData.type,
      features: urbanFeaturePolygonEditionData.features as Feature<LineString>[],
    },
    mode: editableGeoJsonLayerMode,
    selectedFeatureIndexes: currentSelectedIndexes,
    onEdit: (updatedData) =>
      onEditUrbanFeaturePolygonLayer(updatedData as EditAction<FeatureCollection<LineString, UrbanFeature>>),
    visible: isVisibleUrbanFeaturePolygonLayer,
  });

  const polygonLayer = new GeoJsonLayer({
    id: "urban-feature-polygon-layer",
    data: urbanFeaturePolygonData,
    stroked: true,
    lineWidthMaxPixels: 1,
    pickable: urbanFeatureMode === UrbanFeatureModeType.View,
    getLineColor: (object) => object.properties?.lineColor,
    getFillColor: (object) => object.properties?.fillColor,
    onHover: (info) => {
      if (urbanFeatureMode !== UrbanFeatureModeType.View) {
        return;
      }
      if (info.object) {
        if (
          !hoverUrbanFeaturePolygonInfo ||
          (hoverUrbanFeaturePolygonInfo?.urbanFeature &&
            info.object.id !== hoverUrbanFeaturePolygonInfo?.urbanFeature.id)
        ) {
          setHoverUrbanFeaturePolygonInfo({
            left: info.x,
            top: info.y,
            urbanFeature: info.object.properties,
          });
        }
      } else if (!info.object) {
        setHoverUrbanFeaturePolygonInfo(null);
      }
    },
    updateTriggers: {
      onHover: [urbanFeatureMode],
      pickable: [urbanFeatureMode],
    },
  });

  const urbanFeaturePolygonLayers = [polygonLayer, editableUrbanFeaturePolygonLayer];

  useEffect(() => {
    if (!currentCity || urbanFeaturePolygonLoading) {
      return;
    }
    const data = urbanFeatureList.reduce(
      (preValues, urbanFeature) => {
        if (selectedUrbanTypes.length > 0 && !selectedUrbanTypes.includes(urbanFeature.type)) {
          return preValues;
        }
        const lineColor = urbanFeature.type === UrbanFeatureType.Crosswalk ? [54, 240, 171, 255] : [255, 227, 36, 255];
        const fillColor = urbanFeature.type === UrbanFeatureType.Crosswalk ? [54, 240, 171, 56] : [255, 227, 36, 56];
        const newItem = {
          id: urbanFeature.id,
          ...urbanFeature.editLine,
          properties: {
            ...urbanFeature,
            lineColor,
            fillColor,
          },
        } as Feature<LineString, UrbanFeature>;
        return [...preValues, newItem];
      },
      [] as Feature<LineString, UrbanFeature>[],
    );

    setUrbanFeaturePolygonEditionData({
      type: "FeatureCollection",
      features: data,
    });
  }, [urbanFeatureList, urbanFeaturePolygonLoading, currentCity, selectedUrbanTypes]);

  useEffect(() => {
    const listPolygon = urbanFeaturePolygonEditionData.features.map((item) => ({
      type: "Feature",
      geometry: item.properties.geometry,
      properties: item.properties,
      id: item.id,
    })) as Feature<Polygon, UrbanFeature>[];

    setUrbanFeaturePolygonData(listPolygon);
  }, [urbanFeaturePolygonEditionData]);

  return {
    openUrbanFeaturePolygonModal,
    setOpenUrbanFeaturePolygonModal,
    urbanFeaturePolygonDetail,
    setUrbanFeaturePolygonDetail,
    cancelUrbanFeaturePolygonChange,
    urbanFeaturePolygonLayers,
    saveUrbanFeaturePolygon,
    hoverUrbanFeaturePolygonInfo,
    handleRemoveUrbanFeaturePolygon,
  };
};
